import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { CONTACT } from "@/data/translations";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import {
    ArrowLeft, ArrowRight, ArrowUpRight, CalendarCheck, MessageCircle, Mail, RotateCcw, CheckCircle2,
} from "lucide-react";

// Slug del protocollo target per ogni risposta (peso = punti assegnati)
const QUESTIONS = [
    {
        id: "age",
        type: "single",
        options: [
            { id: "u35", weights: {} },
            { id: "35-44", weights: {} },
            { id: "45-54", weights: { "longevita-attiva-over-45": 4 } },
            { id: "55plus", weights: { "longevita-attiva-over-45": 5 } },
        ],
    },
    {
        id: "gender",
        type: "single",
        options: [
            { id: "female", weights: { "equilibrio-femminile": 4 } },
            { id: "male", weights: {} },
            { id: "skip", weights: {} },
        ],
    },
    {
        id: "goal",
        type: "single",
        options: [
            { id: "strength", weights: { "express-performance-20": 2 } },
            { id: "weight", weights: { "reset-metabolico-neuro-wellness": 3, "equilibrio-femminile": 1 } },
            { id: "longevity", weights: { "longevita-attiva-over-45": 5 } },
            { id: "posture", weights: { "postura-riatletizzazione": 6 } },
            { id: "energy", weights: { "reset-metabolico-neuro-wellness": 6, "mind-recovery": 2 } },
            { id: "performance", weights: { "express-performance-20": 4 } },
        ],
    },
    {
        id: "time",
        type: "single",
        options: [
            { id: "lt2", weights: { "express-performance-20": 6 } },
            { id: "2-4", weights: { "express-performance-20": 3 } },
            { id: "4-6", weights: {} },
            { id: "gt6", weights: {} },
        ],
    },
    {
        id: "stress",
        type: "single",
        options: [
            { id: "low", weights: {} },
            { id: "mid", weights: { "mind-recovery": 2 } },
            { id: "high", weights: { "mind-recovery": 5, "reset-metabolico-neuro-wellness": 3 } },
        ],
    },
    {
        id: "mode",
        type: "single",
        options: [
            { id: "in-person", weights: {} },
            { id: "online", weights: { "metodo-online": 3 } },
            { id: "both", weights: { "metodo-online": 1 } },
        ],
    },
];

const computeResult = (answers, protocols) => {
    const scores = {};
    protocols.forEach((p) => { scores[p.slug] = 0; });

    QUESTIONS.forEach((q, qi) => {
        const answerId = answers[qi];
        if (!answerId) return;
        const option = q.options.find((o) => o.id === answerId);
        if (!option) return;
        Object.entries(option.weights).forEach(([slug, pts]) => {
            scores[slug] = (scores[slug] || 0) + pts;
        });
    });

    let bestSlug = protocols[0].slug;
    let bestScore = -1;
    Object.entries(scores).forEach(([slug, score]) => {
        if (score > bestScore) {
            bestScore = score;
            bestSlug = slug;
        }
    });

    // Se nessun punteggio significativo (tutti 0), default soft: Longevità se 45+, altrimenti Express
    if (bestScore === 0) {
        bestSlug = answers[0] === "45-54" || answers[0] === "55plus"
            ? "longevita-attiva-over-45"
            : "express-performance-20";
    }

    const winner = protocols.find((p) => p.slug === bestSlug) || protocols[0];
    return { winner, scores };
};

const ProgressBar = ({ current, total }) => (
    <div className="w-full">
        <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">
                {String(current + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw">
                {Math.round(((current + 1) / total) * 100)}%
            </span>
        </div>
        <div className="h-[2px] w-full bg-white/10 overflow-hidden">
            <div
                className="h-full bg-fw transition-all duration-500 ease-out"
                style={{ width: `${((current + 1) / total) * 100}%` }}
                data-testid="quiz-progress-bar"
            />
        </div>
    </div>
);

const QuestionStep = ({ question, qContent, current, total, value, onSelect, onBack }) => (
    <div data-testid={`quiz-step-${question.id}`} className="space-y-8">
        <ProgressBar current={current} total={total} />
        <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw mb-3">
                {qContent.overline}
            </p>
            <h2 className="font-heavy text-3xl md:text-4xl lg:text-5xl uppercase leading-[1.05] text-balance">
                {qContent.question}
            </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {question.options.map((opt, idx) => {
                const isSelected = value === opt.id;
                return (
                    <button
                        key={opt.id}
                        type="button"
                        onClick={() => onSelect(opt.id)}
                        data-testid={`quiz-option-${question.id}-${opt.id}`}
                        className={`group text-left flex items-center justify-between gap-4 px-5 py-4 border rounded-sm transition-all duration-300 ${
                            isSelected
                                ? "border-fw bg-fw/10 text-white"
                                : "border-white/15 bg-white/[0.02] hover:border-fw/50 hover:bg-fw/[0.04] text-white/85"
                        }`}
                    >
                        <span className="flex items-center gap-3">
                            <span className={`flex-none w-5 h-5 rounded-full border ${isSelected ? "border-fw bg-fw/30" : "border-white/30"} flex items-center justify-center`}>
                                {isSelected && <span className="w-2 h-2 rounded-full bg-fw" />}
                            </span>
                            <span className="font-display text-base">
                                {qContent.options[idx]}
                            </span>
                        </span>
                        <ArrowRight className={`w-4 h-4 transition-transform ${isSelected ? "translate-x-1 text-fw" : "text-white/40 group-hover:translate-x-1"}`} strokeWidth={2} />
                    </button>
                );
            })}
        </div>
        {current > 0 && (
            <button
                type="button"
                onClick={onBack}
                data-testid="quiz-back-button"
                className="inline-flex items-center gap-2 text-white/55 hover:text-fw transition-colors font-mono text-[11px] uppercase tracking-[0.24em]"
            >
                <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                {qContent.backLabel}
            </button>
        )}
    </div>
);

const ResultView = ({ winner, t, onRestart }) => (
    <div data-testid="quiz-result" className="space-y-8">
        <div>
            <div className="flex items-center gap-3 mb-4">
                <span className="flex-none w-9 h-9 rounded-full bg-fw/15 border border-fw/40 flex items-center justify-center text-fw">
                    <CheckCircle2 className="w-5 h-5" strokeWidth={2} />
                </span>
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw">
                    {t.quiz.resultOverline}
                </p>
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-white/55 mb-3">
                {t.method.protocolLabel} · {winner.id}
            </p>
            <h2
                data-testid="quiz-result-name"
                className="font-heavy text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.98] text-balance"
            >
                {winner.name}
            </h2>
            <p className="mt-4 text-fw text-base md:text-lg font-display">
                {winner.tagline}
            </p>
            <p className="mt-5 text-white/75 text-base md:text-lg leading-relaxed max-w-2xl">
                {winner.description}
            </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
            <a
                href={CONTACT.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quiz-cta-whatsapp"
                className="group flex items-center justify-between gap-3 px-5 py-4 bg-fw text-black font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm hover:bg-fw-glow hover:glow-green transition-all duration-300"
            >
                <span className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" strokeWidth={2} />
                    {t.quiz.ctaWhatsapp}
                </span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2} />
            </a>
            <a
                href={CONTACT.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quiz-cta-calendly"
                className="group flex items-center justify-between gap-3 px-5 py-4 bg-white text-black font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm hover:bg-fw hover:glow-green transition-all duration-300"
            >
                <span className="flex items-center gap-2">
                    <CalendarCheck className="w-4 h-4" strokeWidth={2} />
                    {t.quiz.ctaCalendly}
                </span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2} />
            </a>
            <a
                href={CONTACT.emailUrl}
                data-testid="quiz-cta-email"
                className="group flex items-center justify-between gap-3 px-5 py-4 border border-white/25 text-white font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm hover:border-fw hover:text-fw transition-all duration-300"
            >
                <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4" strokeWidth={2} />
                    {t.quiz.ctaEmail}
                </span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2} />
            </a>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-white/10">
            <Link
                to={`/protocollo/${winner.slug}`}
                data-testid="quiz-cta-detail"
                className="inline-flex items-center gap-2 text-fw hover:underline font-display text-sm uppercase tracking-[0.16em]"
            >
                {t.quiz.seeProtocolDetail}
                <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <button
                type="button"
                onClick={onRestart}
                data-testid="quiz-restart-button"
                className="inline-flex items-center gap-2 text-white/55 hover:text-fw transition-colors font-display text-sm uppercase tracking-[0.16em]"
            >
                <RotateCcw className="w-4 h-4" strokeWidth={2} />
                {t.quiz.restart}
            </button>
        </div>
    </div>
);

export const Quiz = () => {
    const { t } = useLanguage();
    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);

    const handleSelect = (optionId) => {
        const next = { ...answers, [current]: optionId };
        setAnswers(next);
        if (current < QUESTIONS.length - 1) {
            setTimeout(() => setCurrent((c) => c + 1), 220);
        } else {
            setTimeout(() => setSubmitted(true), 220);
        }
    };

    const handleBack = () => setCurrent((c) => Math.max(0, c - 1));

    const handleRestart = () => {
        setAnswers({});
        setCurrent(0);
        setSubmitted(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const result = useMemo(
        () => (submitted ? computeResult(answers, t.protocols) : null),
        [submitted, answers, t]
    );

    const qContent = t.quiz.questions[current];
    const question = QUESTIONS[current];

    return (
        <>
            <Navigation />
            <main className="relative min-h-screen bg-black text-white pt-32 md:pt-36 pb-24 overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
                <div className="absolute top-20 right-0 w-[420px] h-[420px] rounded-full bg-fw/10 blur-[150px] pointer-events-none" />

                <div className="relative z-10 mx-auto max-w-[920px] px-5 md:px-10 lg:px-16">
                    <div className="mb-10">
                        <Link
                            to="/"
                            data-testid="quiz-back-home"
                            className="inline-flex items-center gap-2 text-white/55 hover:text-fw transition-colors font-mono text-[11px] uppercase tracking-[0.24em]"
                        >
                            <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                            {t.quiz.backHome}
                        </Link>
                    </div>

                    {!submitted && (
                        <div className="mb-10">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="w-10 h-px bg-fw" />
                                <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-fw">
                                    {t.quiz.overline}
                                </span>
                            </div>
                            <h1
                                data-testid="quiz-headline"
                                className="font-heavy text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.98] text-balance"
                            >
                                {t.quiz.headline}
                            </h1>
                            <p className="mt-5 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
                                {t.quiz.description}
                            </p>
                        </div>
                    )}

                    {submitted && result ? (
                        <ResultView winner={result.winner} t={t} onRestart={handleRestart} />
                    ) : (
                        <QuestionStep
                            question={question}
                            qContent={qContent}
                            current={current}
                            total={QUESTIONS.length}
                            value={answers[current]}
                            onSelect={handleSelect}
                            onBack={handleBack}
                        />
                    )}
                </div>
            </main>
            <Footer />
        </>
    );
};
